<template>
  <ButtonWithPaywall
    :permission-level="PermissionLevel.BASIC"
    :fallback-action="openDashboardPaywallModal"
    :disabled="!filterChanged"
    @click="openChoiceDialog"
  >
    <template #iconLeft>
      <i
        class="fa-duotone fa-bookmark"
        style="
          --fa-primary-color: var(--clr-p1);
          --fa-secondary-color: var(--clr-p3);
        "
      ></i>
    </template>
    Enregistrer ma veille
  </ButtonWithPaywall>

  <PopupDialog
    ref="dashboardModal"
    :title="dashboardModalTitle"
    @close="closeDialog"
    :cancelButton="
      !store.selectedDashboard?.is_custom ||
      store.selectedDashboard?._id === DEFAULT_DASHBOARD_ID
    "
  >
    <template #body>
      <AddEditDashboardForm
        v-if="showDashboardForm"
        ref="dashboardForm"
        :targeted-dashboard-name="
          currentDialog === 'update' ? store.selectedDashboard?.name : undefined
        "
        @submit="handleSubmit($event)"
      />
      <template v-else>
        <p>
          Vous avez modifié des filtres sur base de la veille “{{
            store.selectedDashboard?.name
          }}”.
        </p>
        <div v-if="!store.selectedDashboard?.is_custom" class="radio">
          Souhaitez-vous :
          <CheckboxRadioGroup v-model="formRadio" :options="dashboardForms" />
        </div>
        <div v-else>
          <p>
            Cette veille a été crée par Saper Vedere. Pour la modifier ou créer
            une nouvelle veille à partir de celle-ci, veuillez contacter votre
            interlocuteur.
          </p>
        </div>
      </template>
    </template>
    <template
      #footer
      v-if="
        !store.selectedDashboard?.is_custom ||
        store.selectedDashboard?._id === DEFAULT_DASHBOARD_ID
      "
    >
      <Button v-if="currentDialog === 'create'" @click="submitDashboard"
        >Sauvegarder</Button
      >
      <Button v-else-if="currentDialog === 'update'" @click="submitDashboard"
        >Éditer</Button
      >
      <Button v-else @click="openDashboardForm">Confirmer</Button>
    </template>
  </PopupDialog>

  <PopupDialog
    ref="digestModal"
    :title="showDigestForm ? 'Ajouter un récapitulatif' : 'Veille sauvegardée'"
    @close="closeDialog"
  >
    <template #body>
      <!-- TODO: remove "?? undefined" once AddEditDigestForm is in ts -->
      <AddEditDigestForm
        v-if="showDigestForm"
        :targeted-dashboard="store.createdDashboard ?? undefined"
        ref="digestForm"
      />
      <template v-else>
        <p>Votre veilles a bien été sauvegardée.</p>
        <p>Souhaitez-vous créer un récapitulatif sur base de celle-ci ?</p>
      </template>
    </template>
    <template #footer>
      <Button v-if="showDigestForm" @click="createDigest">Ajouter</Button>
      <Button v-else @click="openDigestForm">Créer un récapitulatif</Button>
    </template>
  </PopupDialog>
</template>

<script setup lang="ts">
import { computed, inject, ref, useTemplateRef } from "vue";
import Button from "@follaw-fr/components/reusable/atoms/Button.vue";
import PopupDialog from "@follaw-fr/components/reusable/atoms/PopupDialog.vue";
import AddEditDashboardForm from "./AddEditDashboardForm.vue";
import AddEditDigestForm from "@follaw-fr/components/alerting/molecules/AddEditDigestForm.vue";
import { useDashboardsStore } from "@follaw-fr/stores/dashboards";
import type { DashboardZoomView } from "@commons/types/api/schema.types";
import ButtonWithPaywall from "@follaw-fr/components/reusable/molecules/ButtonWithPaywall.vue";
import { PermissionLevel } from "@follaw-fr/constants/enums/permission-level.enum";
import { openDashboardPaywallModalKey } from "@follaw-fr/constants/injection-keys";
import CheckboxRadioGroup from "@follaw-fr/components/reusable/molecules/CheckboxRadioGroup.vue";
import { DEFAULT_DASHBOARD_ID } from "@follaw-fr/constants/dashboard.constant";
import { useFilterChangeDetection } from "@follaw-fr/composables/useFilterChangeDetection";

const openDashboardPaywallModal = inject(openDashboardPaywallModalKey);
const store = useDashboardsStore();
const { filterChanged } = useFilterChangeDetection();

const currentDialog = ref<"choice" | "update" | "create">("choice");
const formRadio = ref<"update" | "create">("update");
const dashboardForms = [
  {
    value: "update",
    text: "Mettre à jour la veille sélectionnée",
  },
  {
    value: "create",
    text: "Créer une nouvelle veille",
  },
];

const dashboardModalTitle = computed(() => {
  if (currentDialog.value === "update") {
    return "Editer ma veille";
  } else if (currentDialog.value === "create") {
    return "Sauvegarder ma veille";
  } else {
    return "Enregistrement de veille";
  }
});

const closeDialog = () => {
  showDashboardForm.value = false;
  showDigestForm.value = false;
  currentDialog.value = "choice";
  formRadio.value = "update";
};

const openChoiceDialog = () => {
  // if "Tout le contenu" is selected, open the create form directly as the default dashboard can't be edited
  if (store.selectedDashboard?._id === DEFAULT_DASHBOARD_ID) {
    formRadio.value = "create";
    openDashboardForm();
  }
  dashboardModalRef.value?.open();
};

const openDashboardForm = () => {
  if (formRadio.value === "update") {
    currentDialog.value = "update";
  } else {
    currentDialog.value = "create";
  }
  showDashboardForm.value = true;
};

// CREATE DASHBOARD
const dashboardModalRef = useTemplateRef("dashboardModal");
const showDashboardForm = ref(false);

const dashboardFormRef = useTemplateRef("dashboardForm");
const submitDashboard = () => {
  dashboardFormRef.value?.form?.requestSubmit();
};

const handleSubmit = (formData: DashboardZoomView) => {
  if (formRadio.value === "create") {
    createDashboard(formData);
  } else if (formRadio.value === "update") {
    updateDashboard(formData);
  }
};

const createDashboard = (formData: DashboardZoomView) => {
  if (dashboardFormRef.value?.validate()) {
    store.createDashboard(formData);
    dashboardModalRef.value?.close();
    openDigestDialog();
  }
};

const updateDashboard = (formData: DashboardZoomView) => {
  if (store.selectedDashboard && dashboardFormRef.value?.validate()) {
    store.updateDashboard(store.selectedDashboard._id, formData);
    dashboardModalRef.value?.close();
  }
};

// CREATE DIGEST
const digestModalRef = useTemplateRef("digestModal");
const showDigestForm = ref(false);
const openDigestDialog = () => {
  digestModalRef.value?.open();
};

const openDigestForm = () => {
  showDigestForm.value = true;
};
const digestFormRef = useTemplateRef("digestForm");
const createDigest = () => {
  if (digestFormRef.value?.validate()) {
    digestFormRef.value?.form?.requestSubmit();
    showDigestForm.value = false;
  }
};
</script>

<style scoped>
.radio {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 475px; /* This make the radio wrap into a column */
}
</style>
